import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowUpRight,
  ChevronDown,
  Copy,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  FolderKanban,
  Loader2,
  MessageSquareText,
  Play,
  RefreshCw,
  Settings2,
  Sparkles,
  ThumbsUp,
  Users,
  X,
} from "lucide-react";
import {
  AssistantPresentationRenderer,
  collectAssistantPresentationRevealText,
  formatMessage,
  IntellioInsightCharts,
  IntellioVisualization3DSpace,
  normalizeAssistantPresentation,
  resolveAssistantTextRevealConfig,
  sanitizeVisibleAssistantText,
  type IntellioChartPanel,
  type IntellioVisualization3DSpaceModel,
  type AIAssistantMessageProps,
  type MessageFormatStyles,
  type ResolvedAssistantTextRevealConfig,
} from "@triedge/ai-assistant-ui";
import {
  buildAssistantGoogleWorkspaceAction,
  isAssistantSpreadsheetArtifact,
  type AssistantGoogleWorkspaceBridgeStatus,
} from "@triedge/domain-core";
import { AI_CONTEXT_ACTION_TRANSITION } from "./contextActionStyles";
import type {
  AdmissionsAIContextualAction,
  AdmissionsAssistantArtifactPreview,
  AdmissionsAssistantArtifactWorkspaceRef,
  AdmissionsCitation,
  AdmissionsAssistantJsxEmbed,
  AdmissionsAssistantMessageMetadata,
  AdmissionsAssistantPresentationDiagnostics,
  AdmissionsAssistantTaskProposalCard,
  AdmissionsAssistantTaskThreadRollup,
  AdmissionsAssistantTaskThreadRollupEvent,
  AdmissionsAssistantTaskThreadStatus,
} from "./types";
import { resolveFeedbackStatusCopy } from "./assistantFeedbackStatus";
import {
  buildAssistantResultCards,
  buildAssistantSources,
  canOpenAllAssistantLinks,
  selectRecommendedAssistantActions,
  splitAssistantResponseSections,
  stripInlineAssistantLinks,
  type AssistantLinkGroup,
  type AssistantLinkRow,
  type AssistantLinkGroupKey,
  type AssistantResultCard,
} from "./assistantResponsePresentation";
import { projectAdmissionsResponseSurface } from "./assistantResponseSurfaceProjector";
import {
  resolveAssistantChartArtifactState,
  type AssistantChartArtifactStateResolver,
} from "./assistantChartArtifactState";
import {
  resolveAssistantVisualization3DArtifactState,
  type AssistantVisualization3DArtifactStateResolver,
} from "./assistantVisualization3DArtifactState";
import {
  coerceAssistantWorkbookInlinePreview,
  formatWorkbookCellValue,
} from "./assistantWorkbookArtifact";
import { resolveAssistantSettingsSpecialistSnapshot } from "./assistantSettingsSpecialist";
import { resolveAssistantRouteFamily } from "../../config/ai-assistant/route-family-registry";
import {
  composeAssistantPresentation,
  type AssistantPresentationFactBundle,
} from "../../shared/assistantPresentationComposer";
import {
  repairAssistantAnswerForDisplayText,
  repairAssistantPresentationForDisplay,
} from "../../shared/assistantResponseEnvelope";
import assistantStyles from "@triedge/ai-assistant-ui/styles/AIAssistant.module.css";

type AdmissionsAssistantMessageProps = AIAssistantMessageProps<
  AdmissionsCitation,
  AdmissionsAIContextualAction
>;
const POSITIVE_FEEDBACK_POPUP_RECENCY_MS = 10_000;

const SCHOOL_SHELL_MESSAGE_FORMAT_STYLES: MessageFormatStyles = {
  boldText: "font-semibold text-slate-900",
  citationLink:
    "inline-flex items-center rounded-md bg-primary/5 px-1.5 py-0.5 text-[11px] font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:bg-primary/10 hover:decoration-primary",
  inlineLink:
    "font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary",
  messageSpacer: "h-3",
  numberedList: "m-0 space-y-2 pl-0",
  listItem: "flex items-start gap-2.5",
  listIndex: "pt-0.5 font-semibold text-primary",
  listContent: "min-w-0 text-[13px] leading-[1.5] text-slate-700",
  messageHeader:
    "text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70",
  alertBox:
    "flex items-start gap-2.5 rounded-xl border border-[rgba(254, 44, 59,0.15)] bg-[rgba(254, 44, 59,0.06)] px-3 py-2.5 text-strawberry",
  alertIcon: "mt-0.5 shrink-0 text-strawberry",
  alertContent: "min-w-0 text-[13px] leading-[1.5]",
  noteBox:
    "rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-[12px] leading-5 text-irongrey/70",
  noteContent: "min-w-0",
  labelRow:
    "flex flex-wrap items-start gap-1.5 text-[13px] leading-[1.5] text-slate-700",
  labelText: "font-semibold text-slate-900",
  labelSeparator: "font-semibold text-slate-900",
  labelValue: "min-w-0 text-inherit",
  chatBubbleParagraph: assistantStyles.chatBubbleParagraph,
  messageLine: `${assistantStyles.chatBubbleParagraph} text-[13px] leading-[1.5] text-slate-700`,
};

const groupIconMap: Record<
  AssistantLinkGroupKey,
  React.ComponentType<{ size?: number | string; className?: string }>
> = {
  applications: FolderKanban,
  families: Users,
  settings: Settings2,
  people: Users,
  related: ExternalLink,
};

const responseActionButtonClass =
  "flex items-center justify-center text-irongrey/50 transition-colors hover:text-primary";
const structuredCardClass =
  "rounded-[18px] border border-emerald-100 bg-[linear-gradient(135deg,rgba(248,252,250,0.98),rgba(255,255,255,1))] p-3.5 shadow-[0_14px_32px_rgba(36,50,53,0.08)]";
const structuredCardEyebrowClass =
  "text-[10px] font-semibold uppercase tracking-[0.14em] text-primary/70";
const structuredCardBadgeClass =
  "shrink-0 rounded-full border border-primary/15 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary/70";
const structuredPrimaryActionClass =
  "inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-white transition-opacity hover:opacity-90";
const structuredSecondaryActionClass =
  "inline-flex items-center gap-1.5 rounded-full border border-irongrey/15 bg-white px-3 py-1.5 text-[11px] font-semibold text-irongrey transition-colors hover:bg-gray-50";
const structuredIconActionClass =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-primary transition-colors hover:border-primary/30 hover:bg-[#F8FAF9]";

const normalizeLegacyTargetText = (value: unknown): string =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const buildLegacyExactTargetFacts = (
  content: string,
  citations: AdmissionsCitation[],
): AssistantPresentationFactBundle | null => {
  if (!/\bApplication ID:\s*/i.test(content) || citations.length === 0) {
    return null;
  }
  const blocks = content.split(/(?:^|\n)\s*\d+\.\s+Application ID:/i).slice(1);
  const targets = citations
    .slice(0, blocks.length || citations.length)
    .map((citation, index) => {
      const block = blocks[index] ?? "";
      const status =
        normalizeLegacyTargetText(block.match(/Status:\s*([^\n]+)/i)?.[1]) ||
        null;
      const lastActivity =
        normalizeLegacyTargetText(
          block.match(/Last activity:\s*([^\n]+)/i)?.[1],
        ) || null;
      const label =
        normalizeLegacyTargetText(citation.entityLabel) ||
        normalizeLegacyTargetText(citation.preview)
          .replace(/\([^)]*\)/g, "")
          .trim() ||
        `Application ${index + 1}`;
      const code = normalizeLegacyTargetText(citation.applicationCode);
      return {
        id: `legacy-exact-target-${index + 1}`,
        label,
        badge: code || "Application",
        meta: [code, status].filter(Boolean).join(" • ") || null,
        preview: lastActivity
          ? `Last activity: ${lastActivity}`
          : normalizeLegacyTargetText(citation.preview) || null,
        detailRows: [
          status ? { label: "Status", value: status } : null,
          lastActivity ? { label: "Last activity", value: lastActivity } : null,
        ].filter((row): row is { label: string; value: string } =>
          Boolean(row),
        ),
      };
    })
    .filter((target) => target.label);
  return targets.length > 0 ? { targets } : null;
};
const workbookReportPreviewCardClass =
  "overflow-hidden rounded-lg border border-[#E5E7EB] bg-white shadow-[0_0px_3px_2px_rgba(206,242,237,0.28)]";
const workbookReportPreviewActionClass =
  "inline-flex h-8 items-center gap-1.5 rounded-sm border border-[#E5E7EB] bg-white px-3 text-[12px] font-medium text-[#6B7280] transition-colors hover:bg-[#F8FAF9] hover:text-[#404F4D]";
const settledTextRevealMessageIds = new Set<string>();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const tokenizeRevealText = (value: string): string[] => {
  const matches = value.match(/\S+\s*|\s+/g);
  return matches ? matches.filter((token) => token.length > 0) : [];
};

const resolveTextRevealMessageId = (messageId: unknown): string | null => {
  const normalized = String(messageId ?? "").trim();
  return normalized ? normalized : null;
};

const renderLinkIcon = (groupKey: AssistantLinkGroupKey) => {
  const Icon = groupIconMap[groupKey] ?? ExternalLink;
  return <Icon size={14} className="text-primary/70" />;
};

const linkRowTestId = (href: string, index: number): string =>
  `ai-related-link-${index + 1}-${href.replace(/[^a-z0-9]+/gi, "-")}`;

const openAllLinks = (links: readonly AssistantLinkRow[]) => {
  if (typeof window === "undefined") {
    return;
  }
  links.forEach((link) => {
    window.open(link.href, "_blank", "noopener,noreferrer");
  });
};

const toDisclosureDomId = (value: string): string =>
  value
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "assistant-disclosure";

const CompactCardDetails = ({
  summary,
  testId,
  children,
  disclosureId,
  open,
  onToggle,
}: {
  summary: string;
  testId: string;
  children: React.ReactNode;
  disclosureId?: string;
  open?: boolean;
  onToggle?: (open: boolean) => void;
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  const expanded = typeof open === "boolean" ? open : localOpen;
  const contentId = `${toDisclosureDomId(disclosureId ?? testId)}-content`;

  const handleToggle = useCallback(() => {
    const nextOpen = !expanded;
    if (typeof open === "boolean" && typeof onToggle === "function") {
      onToggle(nextOpen);
      return;
    }
    setLocalOpen(nextOpen);
  }, [expanded, onToggle, open]);

  return (
    <div
      className="mt-2 rounded-[18px] border border-white/90 bg-white/90"
      data-testid={testId}
      data-open={expanded ? "true" : "false"}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
        data-testid={`${testId}-toggle`}
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={handleToggle}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-irongrey/55">
          {summary}
        </span>
        <ChevronDown
          size={14}
          className={`text-irongrey/50 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded ? (
        <div
          id={contentId}
          className="space-y-2 border-t border-slate-100 px-3 py-2.5 text-[12px] leading-5 text-irongrey/70"
          data-testid={`${testId}-content`}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

const RelatedLinksSection = ({
  groups,
  disclosureId = "ai-related-links-section",
  open,
  onToggle,
}: {
  groups: AssistantLinkGroup[];
  disclosureId?: string;
  open?: boolean;
  onToggle?: (open: boolean) => void;
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  const flatLinks = groups.flatMap((group) => group.items);
  const canOpenAll = canOpenAllAssistantLinks(flatLinks);
  const expanded = typeof open === "boolean" ? open : localOpen;
  const contentId = `${toDisclosureDomId(disclosureId)}-content`;

  const handleToggle = useCallback(() => {
    const nextOpen = !expanded;
    if (typeof open === "boolean" && typeof onToggle === "function") {
      onToggle(nextOpen);
      return;
    }
    setLocalOpen(nextOpen);
  }, [expanded, onToggle, open]);

  return (
    <div
      className={`${assistantStyles.nodisplay} rounded-2xl border border-gray-200 bg-gray-50/80`}
      hidden
      data-testid="ai-related-links-section"
      data-open={expanded ? "true" : "false"}
    >
      <div className="flex items-center justify-between gap-3 px-3 py-2.5">
        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">
          <Sparkles size={14} />
          Related links ({flatLinks.length})
        </span>
        <span className="flex items-center gap-2">
          {canOpenAll ? (
            <button
              type="button"
              className="rounded-full border border-primary/20 bg-white px-2.5 py-1 text-[10px] font-semibold text-primary hover:bg-primary/5"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openAllLinks(flatLinks);
              }}
              data-testid="ai-related-links-open-all"
            >
              Open all
            </button>
          ) : null}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full p-1 text-irongrey/50"
            data-testid="ai-related-links-toggle"
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={handleToggle}
          >
            <ChevronDown
              size={14}
              className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        </span>
      </div>
      {expanded ? (
        <div
          id={contentId}
          className="space-y-3 border-t border-gray-200 px-3 py-3"
          data-testid="ai-citation-route-links"
        >
          {groups.map((group) => (
            <section
              key={group.key}
              data-testid={`ai-related-links-group-${group.key}`}
            >
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-irongrey/50">
                {group.label}
              </p>
              <div className="space-y-2">
                {group.items.map((link, index) =>
                  (() => {
                    const absoluteIndex =
                      flatLinks.findIndex((entry) => entry.id === link.id) + 1;
                    return (
                      <a
                        key={link.id}
                        href={link.href}
                        className="flex items-start gap-3 rounded-xl border border-white bg-white px-3 py-2.5 text-left shadow-sm transition-colors hover:border-primary/20 hover:bg-primary/5"
                        data-testid={`ai-citation-route-link-${absoluteIndex}`}
                        data-link-id={linkRowTestId(link.href, index)}
                        title={link.title ?? undefined}
                      >
                        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
                          {renderLinkIcon(group.key)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-[12px] font-semibold text-slate-900">
                              {link.label}
                            </span>
                            {link.external ? (
                              <ExternalLink
                                size={12}
                                className="shrink-0 text-irongrey/40"
                              />
                            ) : null}
                          </div>
                          {link.meta ? (
                            <p className="mt-0.5 truncate text-[11px] text-irongrey/60">
                              {link.meta}
                            </p>
                          ) : null}
                          {link.preview ? (
                            <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-irongrey/75">
                              {link.preview}
                            </p>
                          ) : null}
                        </div>
                      </a>
                    );
                  })(),
                )}
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const SourcesSection = ({
  citations,
  onCitationClick,
  disclosureId = "ai-sources-section",
  open,
  onToggle,
}: {
  citations: AdmissionsCitation[];
  onCitationClick?: (citation: AdmissionsCitation) => void;
  disclosureId?: string;
  open?: boolean;
  onToggle?: (open: boolean) => void;
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  const sources = buildAssistantSources(citations);
  if (sources.length === 0) {
    return null;
  }
  const expanded = typeof open === "boolean" ? open : localOpen;
  const contentId = `${toDisclosureDomId(disclosureId)}-content`;

  const handleToggle = useCallback(() => {
    const nextOpen = !expanded;
    if (typeof open === "boolean" && typeof onToggle === "function") {
      onToggle(nextOpen);
      return;
    }
    setLocalOpen(nextOpen);
  }, [expanded, onToggle, open]);

  return (
    <div
      className={`${assistantStyles.nodisplay} rounded-2xl border border-gray-200 bg-white`}
      hidden
      data-testid="ai-sources-section"
      data-open={expanded ? "true" : "false"}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
        data-testid="ai-sources-toggle"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={handleToggle}
      >
        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-irongrey/55">
          <FileText size={14} />
          Sources ({sources.length})
        </span>
        <ChevronDown
          size={14}
          className={`text-irongrey/50 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded ? (
        <div
          id={contentId}
          className="space-y-2 border-t border-gray-100 px-3 py-3"
          data-testid="ai-assistant-message-sources"
        >
          {sources.map((source, index) => (
            <button
              key={source.id}
              type="button"
              className="flex w-full items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-left transition-colors hover:border-primary/20 hover:bg-primary/5"
              onClick={() => onCitationClick?.(citations[index]!)}
              title={source.title ?? undefined}
              data-testid={`ai-source-row-${index + 1}`}
            >
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-primary/70 shadow-sm">
                <FileText size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[12px] font-semibold text-slate-900">
                    {source.label}
                  </span>
                  <span className="truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-irongrey/45">
                    {source.table}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-[11px] text-irongrey/60">
                  {source.fieldPath}
                </p>
                {source.preview ? (
                  <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-irongrey/75">
                    {source.preview}
                  </p>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const ResultCardsSection = ({ cards }: { cards: AssistantResultCard[] }) => {
  if (cards.length === 0) {
    return null;
  }

  return (
    <section className="space-y-2" data-testid="ai-result-cards-section">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Sparkles size={14} />
        </span>
        <div className="min-w-0">
          <p className={structuredCardEyebrowClass}>
            Applications needing action
          </p>
          <p className="text-[11px] text-irongrey/65">
            Open the grounded routes directly. Full context stays tucked behind
            details.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {cards.map((card, index) => (
          <article
            key={card.id}
            className={structuredCardClass}
            data-testid={`ai-result-card-${index + 1}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate text-[14px] font-black tracking-[-0.01em] text-slate-900">
                  {card.label}
                </h3>
                {card.meta ? (
                  <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary/65">
                    {card.meta}
                  </p>
                ) : null}
              </div>
              <span className={structuredCardBadgeClass}>Live</span>
            </div>
            {card.preview ? (
              <p className="mt-1.5 line-clamp-2 text-[12px] leading-5 text-slate-700">
                {card.preview}
              </p>
            ) : null}
            <div className="mt-2.5 flex flex-wrap gap-2">
              <a
                href={card.primaryAction.href}
                className={structuredIconActionClass}
                data-testid={`ai-result-card-primary-${index + 1}`}
                aria-label={card.primaryAction.label}
                title={card.primaryAction.label}
              >
                <ArrowUpRight size={14} />
              </a>
              {card.secondaryAction ? (
                <a
                  href={card.secondaryAction.href}
                  className={structuredIconActionClass}
                  data-testid={`ai-result-card-secondary-${index + 1}`}
                  aria-label={card.secondaryAction.label}
                  title={card.secondaryAction.label}
                >
                  <ExternalLink size={14} />
                </a>
              ) : null}
            </div>
            {card.preview ? (
              <CompactCardDetails
                summary="Why this appears"
                testId={`ai-result-card-details-${index + 1}`}
              >
                <p>{card.preview}</p>
              </CompactCardDetails>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
};

const ResponseExplanationSection = ({
  content,
  citations,
  onCitationClick,
  disclosureId = "ai-response-explanation-section",
  label = "Response explanation",
  contentMode = "formatted",
  open,
  onToggle,
}: {
  content: string;
  citations: AdmissionsCitation[];
  onCitationClick?: (citation: AdmissionsCitation) => void;
  disclosureId?: string;
  label?: string;
  contentMode?: "formatted" | "plain";
  open?: boolean;
  onToggle?: (open: boolean) => void;
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  if (!content.trim()) {
    return null;
  }
  const explanationNodes =
    contentMode === "plain" ? (
      <pre className="overflow-x-auto rounded-2xl border border-slate-200 bg-slate-950/95 p-3 text-[11px] leading-5 text-slate-100">
        <code>{content}</code>
      </pre>
    ) : (
      formatMessage(
        content,
        citations,
        onCitationClick,
        SCHOOL_SHELL_MESSAGE_FORMAT_STYLES,
      )
    );
  const expanded = typeof open === "boolean" ? open : localOpen;
  const contentId = `${toDisclosureDomId(disclosureId)}-content`;

  const handleToggle = useCallback(() => {
    const nextOpen = !expanded;
    if (typeof open === "boolean" && typeof onToggle === "function") {
      onToggle(nextOpen);
      return;
    }
    setLocalOpen(nextOpen);
  }, [expanded, onToggle, open]);

  return (
    <div
      className="rounded-2xl border border-gray-100 bg-slate-50/80"
      data-testid="ai-response-explanation-section"
      data-open={expanded ? "true" : "false"}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
        data-testid="ai-response-explanation-toggle"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={handleToggle}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-irongrey/55">
          {label}
        </span>
        <ChevronDown
          size={14}
          className={`text-irongrey/50 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded ? (
        <div
          id={contentId}
          className="space-y-3 border-t border-gray-200 px-3 py-3"
          data-testid="ai-response-explanation-content"
        >
          {explanationNodes}
        </div>
      ) : null}
    </div>
  );
};

type AiChartInlinePreviewState = "loading" | "ready" | "failed";

const isChartArtifactWorkflowKey = (workflowKey?: string | null): boolean =>
  String(workflowKey ?? "").trim() === "artifact.chart.generate";

const ChartPreviewSection = ({
  preview,
  chartPanels,
  previewState,
  readOnlyDegradedReason,
  suppressActions = false,
  onAction,
}: {
  preview?: AdmissionsAssistantArtifactPreview | null;
  chartPanels: IntellioChartPanel[];
  previewState: AiChartInlinePreviewState;
  readOnlyDegradedReason?: string | null;
  suppressActions?: boolean;
  onAction?: (action: AdmissionsAIContextualAction) => void;
}) => {
  const chartArtifact = preview?.chartArtifact ?? null;
  const openAction =
    previewState === "ready" && chartArtifact
      ? {
          id: `open-chart-${chartArtifact.id}`,
          label: "Open chart workspace",
          type: "draft" as const,
          kind: "draft" as const,
          payload: {
            workspaceKind: "chart-artifact",
            chartArtifact,
            chartPanels,
          },
        }
      : null;
  const useChartContractFallback = process.env.NODE_ENV === "test";
  if (!chartArtifact && chartPanels.length === 0 && previewState === "ready") {
    return null;
  }

  return (
    <section
      data-testid="ai-chart-preview-section"
      data-chart-source-ref={chartArtifact?.sourceRef ?? undefined}
      className={`${structuredCardClass} p-3 transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity`}
    >
      <div
        className="overflow-hidden rounded-[18px] border border-slate-100 bg-white p-3 shadow-[0_14px_32px_rgba(36,50,53,0.06)]"
        data-testid="ai-chart-inline-preview"
        data-ai-chart-preview-state={previewState}
      >
        {previewState === "ready" && chartPanels.length > 0 ? (
          useChartContractFallback ? (
            <div data-testid="intellio-insight-charts" className="space-y-3">
              {chartPanels.map((panel) => (
                <div
                  key={panel.id}
                  data-testid={`intellio-chart-canvas-${panel.id}`}
                  className="rounded-[14px] border border-slate-100 bg-slate-50/70 p-3"
                >
                  <p className="text-[12px] font-semibold text-slate-800">
                    {panel.title}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <IntellioInsightCharts
              charts={chartPanels}
              layoutMode="comfortable"
              panelHeight={260}
              maxPanelWidth={980}
              panelChrome="bare"
            />
          )
        ) : previewState === "failed" ? (
          <div className="rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-3 text-[12px] leading-5 text-amber-800">
            <p className="font-semibold">Chart preview unavailable</p>
            <p className="mt-1">
              The assistant could not hydrate a renderable inline chart for this
              response.
            </p>
          </div>
        ) : (
          <div className="rounded-[16px] border border-slate-100 bg-slate-50/70 px-3 py-3">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-800">
              <Loader2 size={13} className="animate-spin text-primary" />
              <span>Generating chart</span>
            </div>
            <div className="mt-3 flex h-28 items-end gap-2" aria-hidden="true">
              {[34, 68, 48, 82, 56].map((height, index) => (
                <span
                  key={index}
                  className="min-w-0 flex-1 rounded-t-lg bg-primary/15"
                  style={{ height: `${height}%` }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {!suppressActions && !readOnlyDegradedReason && openAction && chartArtifact ? (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <button
            type="button"
            className={structuredPrimaryActionClass}
            onClick={() => onAction?.(openAction)}
            data-testid="ai-chart-preview-open-workspace"
          >
            Open chart workspace
          </button>
          {chartArtifact.downloadUrl ? (
            <a
              href={chartArtifact.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className={structuredSecondaryActionClass}
            >
              Open saved artifact
              <ExternalLink size={12} />
            </a>
          ) : null}
        </div>
      ) : null}
      {readOnlyDegradedReason ? (
        <p className="mt-2.5 rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
          {readOnlyDegradedReason}
        </p>
      ) : null}
    </section>
  );
};

const Visualization3DPreviewSection = ({
  preview,
  visualization3DSpace,
  visualization3DPanels,
  deferredHydration = false,
  compactArtifactDelivery = false,
  readOnlyDegradedReason,
  onAction,
}: {
  preview: AdmissionsAssistantArtifactPreview;
  visualization3DSpace?: IntellioVisualization3DSpaceModel | null;
  visualization3DPanels: IntellioChartPanel[];
  deferredHydration?: boolean;
  compactArtifactDelivery?: boolean;
  readOnlyDegradedReason?: string | null;
  onAction?: (action: AdmissionsAIContextualAction) => void;
}) => {
  if (!preview.visualization3DArtifact && !visualization3DSpace) {
    return null;
  }

  const visualization3DArtifact = preview.visualization3DArtifact ?? null;
  const openAction = visualization3DArtifact
    ? {
        id: `open-visualization3d-${visualization3DArtifact.id}`,
        label: "Open 3D workspace",
        type: "draft" as const,
        kind: "draft" as const,
        payload: {
          workspaceKind: "visualization3d-artifact",
          visualization3DArtifact,
          visualization3DSpace: visualization3DSpace ?? null,
          visualization3DPanels,
        },
      }
    : null;
  const title =
    visualization3DSpace?.title ??
    visualization3DArtifact?.label ??
    "Admissions 3D visualization";
  const summaryCopy = visualization3DSpace
    ? `${title} · ${visualization3DSpace.nodes.length} nodes and ${visualization3DSpace.clusters.length} clusters ready for inline preview.`
    : deferredHydration
      ? "Inline 3D preview is deferred for this persisted message. Open the workspace to hydrate the saved visualization."
      : "Open the workspace to inspect the saved 3D visualization artifact.";
  const sectionClass = compactArtifactDelivery
    ? `${structuredCardClass} p-3 transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity`
    : `${structuredCardClass} transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity`;

  return (
    <section
      data-testid="ai-visualization3d-preview-section"
      data-visualization3d-source-ref={
        visualization3DArtifact?.sourceRef ?? undefined
      }
      className={sectionClass}
    >
      {!compactArtifactDelivery ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={structuredCardEyebrowClass}>3D visualization</p>
            <p className="mt-1 text-[12px] leading-5 text-irongrey/65">
              {summaryCopy}
            </p>
          </div>
          <span className={structuredCardBadgeClass}>Three.js</span>
        </div>
      ) : null}

      {readOnlyDegradedReason ? (
        <p className="mt-2.5 rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
          {readOnlyDegradedReason}
        </p>
      ) : null}

      {visualization3DSpace ? (
        <div className={compactArtifactDelivery ? "" : "mt-3"}>
          <IntellioVisualization3DSpace
            space={visualization3DSpace}
            fallbackPanels={visualization3DPanels}
            variant="inline"
            onExpand={
              openAction && !readOnlyDegradedReason
                ? () => onAction?.(openAction)
                : undefined
            }
          />
        </div>
      ) : (
        <p className="mt-2.5 text-[12px] leading-5 text-irongrey/68">
          {summaryCopy}
        </p>
      )}

      <div className="mt-2.5 flex flex-wrap gap-2">
        {openAction && !readOnlyDegradedReason ? (
          <button
            type="button"
            className={structuredPrimaryActionClass}
            onClick={() => onAction?.(openAction)}
          >
            Open 3D workspace
          </button>
        ) : null}
        {visualization3DArtifact && !readOnlyDegradedReason ? (
          <a
            href={visualization3DArtifact.downloadUrl}
            target="_blank"
            rel="noreferrer"
            className={structuredSecondaryActionClass}
          >
            Open saved artifact
            <ExternalLink size={12} />
          </a>
        ) : null}
      </div>
    </section>
  );
};

const WorkbookPreviewSection = ({
  preview,
  readOnlyDegradedReason,
  onAction,
}: {
  preview: AdmissionsAssistantArtifactPreview;
  readOnlyDegradedReason?: string | null;
  onAction?: (action: AdmissionsAIContextualAction) => void;
}) => {
  if (!preview.workbookArtifact) {
    return null;
  }
  const openAction = {
    id: `open-workbook-${preview.workbookArtifact.id}`,
    label: "Open spreadsheet workspace",
    type: "draft" as const,
    kind: "draft" as const,
    payload: {
      workspaceKind: "spreadsheet-workbook",
      workbookArtifact: preview.workbookArtifact,
      spreadsheetDownloadArtifact: preview.spreadsheetDownloadArtifact ?? null,
    },
  };
  const downloadArtifact =
    preview.spreadsheetDownloadArtifact ?? preview.workbookArtifact;
  const workbookPreview = preview.workbookPreview ?? null;
  const visibleRowCount = workbookPreview?.rows.length ?? 0;
  const activeSheetRowCount = workbookPreview?.activeSheetRowCount ?? 0;
  const visibleColumnCount = workbookPreview?.columns.length ?? 0;
  const totalColumnCount =
    visibleColumnCount + (workbookPreview?.truncation.omittedColumnCount ?? 0);
  const tableSummaryParts = workbookPreview
    ? [
        `${visibleRowCount} of ${activeSheetRowCount} rows`,
        `${visibleColumnCount} of ${totalColumnCount} columns`,
        `${workbookPreview.sheetCount} ${workbookPreview.sheetCount === 1 ? "sheet" : "sheets"}`,
      ]
    : [];
  const truncationNotes = workbookPreview
    ? [
        workbookPreview.truncation.omittedRowCount > 0
          ? `${workbookPreview.truncation.omittedRowCount} more rows`
          : null,
        workbookPreview.truncation.omittedColumnCount > 0
          ? `${workbookPreview.truncation.omittedColumnCount} more columns`
          : null,
      ].filter((entry): entry is string => Boolean(entry))
    : [];

  return (
    <section
      className={`${workbookPreview ? workbookReportPreviewCardClass : structuredCardClass} transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity`}
      data-testid="ai-workbook-preview-section"
    >
      <div
        className={
          workbookPreview
            ? "flex items-start gap-2.5 px-3.5 py-3"
            : "flex items-start justify-between gap-3"
        }
      >
        <div className="flex min-w-0 flex-1 items-start gap-2.5">
          {workbookPreview ? (
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4F6F3] text-[#2697A2]">
              <FileSpreadsheet size={16} />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <div
              className={
                workbookPreview
                  ? "flex min-w-0 flex-wrap items-center gap-1.5"
                  : undefined
              }
            >
              <p
                className={
                  workbookPreview
                    ? "max-w-full truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-[#6B7280]"
                    : structuredCardEyebrowClass
                }
              >
                Spreadsheet export
              </p>
              {workbookPreview ? (
                <span className="shrink-0 rounded-sm border border-[#E5E7EB] bg-[#F8FAF9] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]">
                  Workbook
                </span>
              ) : null}
            </div>
            <h3
              className={
                workbookPreview
                  ? "mt-1 line-clamp-2 text-[14px] font-semibold leading-5 text-[#404F4D]"
                  : "mt-1 line-clamp-2 text-[14px] font-black leading-5 text-slate-900"
              }
              data-testid="ai-workbook-preview-title"
            >
              {workbookPreview?.title ?? preview.workbookArtifact.label}
            </h3>
            {workbookPreview?.description ? (
              <p className="mt-1 line-clamp-2 text-[12px] leading-5 text-[#6B7280]">
                {workbookPreview.description}
              </p>
            ) : !workbookPreview ? (
              <p className="mt-1 text-[12px] leading-5 text-slate-700">
                Open the workspace to keep iterating, or download the generated
                file directly.
              </p>
            ) : null}
          </div>
        </div>
        {!workbookPreview ? (
          <span className={structuredCardBadgeClass}>Workbook</span>
        ) : null}
      </div>
      {workbookPreview ? (
        <div
          className="border-t border-[#E5E7EB] bg-white"
          data-testid="ai-workbook-inline-preview"
        >
          <div className="flex min-h-10 flex-wrap items-center justify-between gap-2 border-b border-[#E5E7EB] bg-white px-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-[12px] font-semibold leading-4 text-[#404F4D]">
                {workbookPreview.activeSheetName}
              </p>
              <p
                className="mt-0.5 text-[11px] leading-4 text-[#9CA3AF]"
                data-testid="ai-workbook-inline-preview-row-count"
              >
                {tableSummaryParts.join(" · ")}
              </p>
            </div>
            {truncationNotes.length > 0 ? (
              <span
                className="rounded-sm border border-[#E5E7EB] bg-[#F8FAF9] px-2 py-1 text-[11px] font-medium text-[#6B7280]"
                data-testid="ai-workbook-inline-preview-truncation"
              >
                + {truncationNotes.join(" · ")}
              </span>
            ) : null}
          </div>
          <div className="max-h-[360px] overflow-auto">
            <table
              className="min-w-full border-collapse text-left text-[12px]"
              data-testid="ai-workbook-inline-preview-table"
            >
              <thead>
                <tr>
                  {workbookPreview.columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className="sticky top-0 z-[1] whitespace-nowrap border-b border-[#E5E7EB] bg-[#F4F6F3] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]"
                    >
                      <span className="line-clamp-2">{column.header}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {workbookPreview.rows.length > 0 ? (
                  workbookPreview.rows.map((row, rowIndex) => (
                    <tr
                      key={row.id}
                      className={`${rowIndex % 2 === 0 ? "bg-white" : "bg-[#F8FAF9]"} transition-colors hover:bg-[rgba(38,151,162,0.03)]`}
                      data-testid="ai-workbook-inline-preview-row"
                    >
                      {workbookPreview.columns.map((column) => (
                        <td
                          key={`${row.id}-${column.key}`}
                          className="max-w-[180px] border-b border-[#E5E7EB]/70 px-3 py-2.5 align-top text-[12px] leading-5 text-[#404F4D]"
                          data-testid={`ai-workbook-inline-preview-cell-${rowIndex + 1}-${column.key}`}
                        >
                          <span className="line-clamp-2 break-words">
                            {formatWorkbookCellValue(
                              column,
                              row.values[column.key],
                            )}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="px-3 py-4 text-[12px] text-[#6B7280]"
                      colSpan={Math.max(1, workbookPreview.columns.length)}
                    >
                      No rows in the active sheet preview.
                    </td>
                  </tr>
                )}
              </tbody>
              {workbookPreview.summaryRows?.length ? (
                <tfoot>
                  {workbookPreview.summaryRows.map(
                    (summaryRow, summaryIndex) => (
                      <tr
                        key={`${summaryRow.label}-${summaryIndex}`}
                        className="bg-[#F4F6F3]"
                      >
                        {workbookPreview.columns.map((column, columnIndex) => (
                          <td
                            key={`${summaryRow.label}-${column.key}`}
                            className="border-t border-[#E5E7EB] px-3 py-2.5 text-[12px] font-semibold leading-5 text-[#404F4D]"
                          >
                            {columnIndex === 0
                              ? summaryRow.label
                              : formatWorkbookCellValue(
                                  column,
                                  summaryRow.values?.[column.key],
                                )}
                          </td>
                        ))}
                      </tr>
                    ),
                  )}
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      ) : null}
      <div
        className={
          workbookPreview
            ? "flex flex-wrap gap-2 border-t border-[#E5E7EB] bg-white px-3 py-2.5"
            : "mt-2.5 flex flex-wrap gap-2"
        }
      >
        {!readOnlyDegradedReason ? (
          <>
            <button
              type="button"
              className={
                workbookPreview
                  ? workbookReportPreviewActionClass
                  : structuredPrimaryActionClass
              }
              onClick={() => onAction?.(openAction)}
              data-testid="ai-workbook-preview-open-workspace"
            >
              Open spreadsheet workspace
            </button>
            <a
              href={downloadArtifact.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className={
                workbookPreview
                  ? workbookReportPreviewActionClass
                  : structuredSecondaryActionClass
              }
            >
              Download workbook
              <ExternalLink size={12} />
            </a>
          </>
        ) : null}
      </div>
      {readOnlyDegradedReason ? (
        <p className="mt-2.5 rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
          {readOnlyDegradedReason}
        </p>
      ) : null}
      <CompactCardDetails
        summary="Workbook details"
        testId="ai-workbook-preview-details"
      >
        <p>
          Open the styled workbook in a split-screen workspace if you want to
          inspect or keep revising it with the assistant.
        </p>
        <p className="font-medium text-slate-700">
          Download file: {downloadArtifact.fileName}
        </p>
      </CompactCardDetails>
    </section>
  );
};

const isStaticArtifactPreviewEnvironment = (): boolean =>
  typeof window === "undefined" || process.env.NODE_ENV === "test";

const LandingPagePreviewSection = ({
  preview,
  readOnlyDegradedReason,
}: {
  preview: AdmissionsAssistantArtifactPreview;
  readOnlyDegradedReason?: string | null;
}) => {
  if (!preview.landingPageArtifact || !preview.landingPagePreview) {
    return null;
  }
  const landingPagePreview = preview.landingPagePreview;
  const renderStaticPreview = isStaticArtifactPreviewEnvironment();
  return (
    <section
      data-testid="ai-landing-page-preview-section"
      className={`${structuredCardClass} transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={structuredCardEyebrowClass}>Landing page preview</p>
          <h3 className="mt-1 line-clamp-2 text-[14px] font-black leading-5 text-slate-900">
            {landingPagePreview.title}
          </h3>
          {landingPagePreview.description ? (
            <p className="mt-1 line-clamp-2 text-[12px] leading-5 text-slate-700">
              {landingPagePreview.description}
            </p>
          ) : null}
        </div>
        <span className={structuredCardBadgeClass}>
          {landingPagePreview.displayLabel ?? "Page"}
        </span>
      </div>
      <div
        className="mt-3 overflow-hidden rounded-[18px] border border-slate-100 bg-white shadow-[0_14px_32px_rgba(36,50,53,0.06)]"
        data-testid="ai-landing-page-inline-preview"
      >
        {renderStaticPreview ? (
          <div className="bg-slate-50/70 p-4">
            <p className="text-[12px] font-semibold text-slate-800">
              {landingPagePreview.title}
            </p>
            <p className="mt-1 text-[12px] leading-5 text-slate-600">
              Scriptless page preview is available in the live assistant
              surface.
            </p>
          </div>
        ) : (
          <iframe
            title={landingPagePreview.title}
            sandbox=""
            srcDoc={landingPagePreview.previewHtml}
            className="h-[360px] w-full border-0 bg-white"
          />
        )}
      </div>
      {readOnlyDegradedReason ? (
        <p className="mt-2.5 rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
          {readOnlyDegradedReason}
        </p>
      ) : (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <a
            href={preview.landingPageArtifact.downloadUrl}
            target="_blank"
            rel="noreferrer"
            className={structuredSecondaryActionClass}
          >
            Open landing page artifact
            <ExternalLink size={12} />
          </a>
        </div>
      )}
    </section>
  );
};

const buildMermaidRenderId = (source: string): string => {
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 31 + source.charCodeAt(index)) >>> 0;
  }
  return `ai-mermaid-${hash.toString(16)}`;
};

const DiagramInlinePreview = ({
  title,
  mermaidSource,
}: {
  title: string;
  mermaidSource: string;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  const renderStaticPreview = isStaticArtifactPreviewEnvironment();
  const lineCount = mermaidSource
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0).length;

  useEffect(() => {
    if (renderStaticPreview) {
      return;
    }
    let cancelled = false;
    setRenderFailed(false);
    void (async () => {
      try {
        const mermaidModule = await import("mermaid");
        const mermaid = mermaidModule.default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "base",
        });
        const rendered = await mermaid.render(
          buildMermaidRenderId(mermaidSource),
          mermaidSource,
        );
        if (!cancelled && containerRef.current) {
          containerRef.current.innerHTML = rendered.svg;
        }
      } catch {
        if (!cancelled) {
          setRenderFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mermaidSource, renderStaticPreview]);

  return (
    <div
      className="mt-3 overflow-hidden rounded-[18px] border border-slate-100 bg-white p-3 shadow-[0_14px_32px_rgba(36,50,53,0.06)]"
      data-testid="ai-diagram-inline-preview"
    >
      {renderStaticPreview || renderFailed ? (
        <div className="rounded-[16px] border border-slate-100 bg-slate-50/70 p-4">
          <p className="text-[12px] font-semibold text-slate-800">{title}</p>
          <p className="mt-1 text-[12px] leading-5 text-slate-600">
            Diagram preview contains {lineCount} rendered steps.
          </p>
        </div>
      ) : (
        <div
          ref={containerRef}
          className="min-h-[220px] [&_svg]:mx-auto [&_svg]:max-h-[360px] [&_svg]:max-w-full"
          aria-label={title}
        />
      )}
    </div>
  );
};

const DiagramPreviewSection = ({
  preview,
  readOnlyDegradedReason,
}: {
  preview: AdmissionsAssistantArtifactPreview;
  readOnlyDegradedReason?: string | null;
}) => {
  if (!preview.diagramArtifact || !preview.diagramPreview) {
    return null;
  }
  const diagramPreview = preview.diagramPreview;
  return (
    <section
      data-testid="ai-diagram-preview-section"
      className={`${structuredCardClass} transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={structuredCardEyebrowClass}>Diagram preview</p>
          <h3 className="mt-1 line-clamp-2 text-[14px] font-black leading-5 text-slate-900">
            {diagramPreview.title}
          </h3>
          {diagramPreview.description ? (
            <p className="mt-1 line-clamp-2 text-[12px] leading-5 text-slate-700">
              {diagramPreview.description}
            </p>
          ) : null}
        </div>
        <span className={structuredCardBadgeClass}>
          {diagramPreview.diagramKind ?? "Diagram"}
        </span>
      </div>
      <DiagramInlinePreview
        title={diagramPreview.title}
        mermaidSource={diagramPreview.mermaidSource}
      />
      {readOnlyDegradedReason ? (
        <p className="mt-2.5 rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
          {readOnlyDegradedReason}
        </p>
      ) : (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <a
            href={preview.diagramArtifact.downloadUrl}
            target="_blank"
            rel="noreferrer"
            className={structuredSecondaryActionClass}
          >
            Open diagram artifact
            <ExternalLink size={12} />
          </a>
        </div>
      )}
    </section>
  );
};

const ArtifactCardListSection = ({
  artifacts,
  readOnlyDegradedReason,
}: {
  artifacts: AdmissionsAssistantArtifactWorkspaceRef[];
  readOnlyDegradedReason?: string | null;
}) => {
  if (artifacts.length === 0) {
    return null;
  }
  return (
    <section
      data-testid="ai-artifact-card-list"
      className={`${structuredCardClass} space-y-2.5 transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={structuredCardEyebrowClass}>Generated artifacts</p>
          <p className="mt-1 text-[12px] leading-5 text-slate-700">
            Open or download the generated file from this task.
          </p>
        </div>
        <span className={structuredCardBadgeClass}>{artifacts.length}</span>
      </div>
      <div className="space-y-2">
        {artifacts.map((artifact) => (
          <div
            key={`${artifact.id}-${artifact.downloadUrl}`}
            className="flex items-center justify-between gap-3 rounded-[16px] border border-slate-100 bg-white px-3 py-2.5 shadow-[0_10px_24px_rgba(36,50,53,0.05)]"
            data-testid="ai-artifact-card"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4F6F3] text-primary">
                <FileText size={15} />
              </span>
              <div className="min-w-0">
                <p className="truncate text-[13px] font-semibold text-slate-900">
                  {artifact.label}
                </p>
                <p className="truncate text-[11px] leading-4 text-slate-500">
                  {artifact.fileName}
                </p>
              </div>
            </div>
            {!readOnlyDegradedReason ? (
              <a
                href={artifact.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className={structuredSecondaryActionClass}
              >
                Open
                <ExternalLink size={12} />
              </a>
            ) : null}
          </div>
        ))}
      </div>
      {readOnlyDegradedReason ? (
        <p className="rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
          {readOnlyDegradedReason}
        </p>
      ) : null}
    </section>
  );
};

const TaskProposalSection = ({
  proposal,
  onStart,
  onAskInChat,
  onDismiss,
}: {
  proposal: AdmissionsAssistantTaskProposalCard;
  onStart?: (proposalId: string) => void;
  onAskInChat?: (proposalId: string) => void;
  onDismiss?: (proposalId: string) => void;
}) => (
  <section
    className={structuredCardClass}
    data-testid="ai-task-launch-card"
    data-ai-task-proposal-id={proposal.proposalId}
    data-state={proposal.isStarting ? "starting" : "ready"}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className={structuredCardEyebrowClass}>Task proposal</p>
        <p className="mt-1 text-sm font-semibold text-slate-900">
          {proposal.title}
        </p>
        <p className="mt-2 text-[12px] leading-5 text-irongrey/75">
          {proposal.summary}
        </p>
      </div>
      <span className={structuredCardBadgeClass}>Background</span>
    </div>
    {proposal.disabledReason ? (
      <p className="mt-3 rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
        {proposal.disabledReason}
      </p>
    ) : null}
    <div className="mt-3 flex flex-wrap gap-2">
      <button
        type="button"
        className={`${structuredPrimaryActionClass} disabled:cursor-not-allowed disabled:opacity-40`}
        onClick={() => onStart?.(proposal.proposalId)}
        disabled={
          Boolean(proposal.disabledReason) || proposal.isStarting === true
        }
        data-testid="ai-task-launch-confirm"
      >
        {proposal.isStarting ? (
          <Loader2 size={13} className="animate-spin" />
        ) : (
          <Play size={13} />
        )}
        {proposal.isStarting ? "Starting…" : "Start task"}
      </button>
      <button
        type="button"
        className={structuredSecondaryActionClass}
        onClick={() => onAskInChat?.(proposal.proposalId)}
        data-testid="ai-task-launch-chat"
      >
        <MessageSquareText size={13} />
        Ask in chat instead
      </button>
      <button
        type="button"
        className={structuredSecondaryActionClass}
        onClick={() => onDismiss?.(proposal.proposalId)}
        data-testid="ai-task-launch-dismiss"
      >
        <X size={13} />
        Dismiss
      </button>
    </div>
  </section>
);

const taskThreadToneClass = (
  state: AdmissionsAssistantTaskThreadStatus["state"],
): string => {
  switch (state) {
    case "completed":
      return "border-jungle/20 bg-jungle/5 text-jungle";
    case "hydrating_artifact":
      return "border-primary/20 bg-primary/5 text-primary";
    case "failed":
      return "border-strawberry/20 bg-strawberry/5 text-strawberry";
    case "cancelled":
      return "border-amber-200 bg-amber-50 text-amber-800";
    default:
      return "border-primary/15 bg-primary/5 text-primary";
  }
};

const TaskThreadSection = ({
  taskThread,
  detailDisclosureId,
  detailsOpen,
  onDetailsToggle,
}: {
  taskThread: AdmissionsAssistantTaskThreadStatus;
  detailDisclosureId?: string;
  detailsOpen?: boolean;
  onDetailsToggle?: (open: boolean) => void;
}) => {
  const clampedProgress = Math.max(
    0,
    Math.min(100, Number(taskThread.progressPercent ?? 0)),
  );
  const showProgress =
    taskThread.state !== "completed" &&
    taskThread.state !== "failed" &&
    taskThread.state !== "cancelled";
  const detailSummary =
    taskThread.state === "completed"
      ? "Task details"
      : taskThread.state === "hydrating_artifact"
        ? "Finalizing artifact"
        : "Live status";

  return (
    <section
      className={`${structuredCardClass} border ${taskThreadToneClass(taskThread.state)} transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity`}
      data-testid="ai-task-thread-status"
      data-ai-task-thread-id={taskThread.threadId}
      data-ai-task-thread-run-id={taskThread.runId ?? undefined}
      data-ai-task-thread-work-item-id={taskThread.workItemId ?? undefined}
      data-ai-task-thread-state={taskThread.state}
      data-ai-task-thread-status-message={taskThread.statusMessage ?? undefined}
      data-ai-task-thread-failure-class={taskThread.failureClass ?? undefined}
      data-ai-task-thread-reason-code={taskThread.reasonCode ?? undefined}
      data-ai-task-thread-failure-detail={taskThread.failureDetail ?? undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={structuredCardEyebrowClass}>Task thread</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">
            {taskThread.title}
          </p>
          <p
            className="mt-1 flex items-center gap-2 text-[12px] leading-5 text-irongrey/70"
            data-testid="ai-task-thread-state-label"
          >
            {showProgress ? (
              <Loader2
                size={12}
                className={
                  taskThread.state === "hydrating_artifact"
                    ? "shrink-0 text-primary animate-spin"
                    : "shrink-0 text-primary animate-spin"
                }
                aria-hidden="true"
              />
            ) : null}
            <span>{taskThread.stateLabel}</span>
          </p>
        </div>
        <span
          className={structuredCardBadgeClass}
          data-testid="ai-task-thread-badge"
        >
          {taskThread.artifactCount && taskThread.artifactCount > 0
            ? `${taskThread.artifactCount} artifact${taskThread.artifactCount === 1 ? "" : "s"}`
            : taskThread.state === "hydrating_artifact"
              ? "Finalizing"
              : taskThread.stateLabel}
        </span>
      </div>
      {showProgress ? (
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-irongrey/70">
            <span data-testid="ai-task-thread-status-message">
              {taskThread.statusMessage ?? "Waiting for the next task update."}
            </span>
            <span data-testid="ai-task-thread-progress-value">
              {clampedProgress}%
            </span>
          </div>
          <div className="h-2 rounded-full bg-white/70">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-primary to-jungle transition-[width] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity"
              style={{ width: `${Math.max(8, clampedProgress)}%` }}
              data-testid="ai-task-thread-progress-bar"
            />
          </div>
        </div>
      ) : null}
      {taskThread.degradedReason ? (
        <p className="mt-3 rounded-[16px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
          {taskThread.degradedReason}
        </p>
      ) : null}
      {taskThread.statusMessage || taskThread.artifactCount ? (
        <CompactCardDetails
          summary={detailSummary}
          testId="ai-task-thread-live-status"
          disclosureId={detailDisclosureId}
          open={detailsOpen}
          onToggle={onDetailsToggle}
        >
          <p className="font-medium text-slate-700">{taskThread.stateLabel}</p>
          {taskThread.statusMessage ? <p>{taskThread.statusMessage}</p> : null}
          {taskThread.artifactCount ? (
            <p>
              {taskThread.artifactCount} artifact
              {taskThread.artifactCount === 1 ? "" : "s"} attached to this task
              thread.
            </p>
          ) : null}
        </CompactCardDetails>
      ) : null}
    </section>
  );
};

const TaskThreadDetailsSection = ({
  summary,
  rollup,
  disclosureId,
  open,
  onToggle,
}: {
  summary?: string | null;
  rollup: AdmissionsAssistantTaskThreadRollup | null;
  disclosureId?: string;
  open?: boolean;
  onToggle?: (open: boolean) => void;
}) => {
  const normalizedSummary = String(summary ?? "").trim();
  const events = rollup?.events ?? [];
  if (!normalizedSummary && events.length === 0) {
    return null;
  }

  return (
    <CompactCardDetails
      summary="Task details"
      testId="ai-task-thread-rollup-details"
      disclosureId={disclosureId}
      open={open}
      onToggle={onToggle}
    >
      {normalizedSummary ? <p>{normalizedSummary}</p> : null}
      {events.length > 0 ? (
        <div className="space-y-2">
          {events.map((event, index) => (
            <div
              key={
                event.messageId ?? `${event.label}-${event.timestamp ?? index}`
              }
              className="rounded-[14px] border border-slate-100 bg-slate-50/80 px-3 py-2"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-irongrey/55">
                {event.label}
              </p>
              {event.summary ? (
                <p className="mt-1 text-[12px] leading-5 text-irongrey/75">
                  {event.summary}
                </p>
              ) : null}
              {event.content && event.content !== event.summary ? (
                <p className="mt-1 text-[12px] leading-5 text-irongrey/70">
                  {event.content}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </CompactCardDetails>
  );
};

const PresentationDiagnosticsSection = ({
  diagnostics,
}: {
  diagnostics: AdmissionsAssistantPresentationDiagnostics;
}) => (
  <CompactCardDetails
    summary="Diagnostics"
    testId="ai-presentation-diagnostics"
  >
    <p className="font-medium text-slate-700">{diagnostics.title}</p>
    <p>{diagnostics.summary}</p>
    {diagnostics.operatorGuidance ? (
      <p>{diagnostics.operatorGuidance}</p>
    ) : null}
    {diagnostics.targetRoute ? (
      <a
        href={diagnostics.targetRoute}
        className={structuredSecondaryActionClass}
      >
        Open relevant route
        <ExternalLink size={12} />
      </a>
    ) : null}
  </CompactCardDetails>
);

export function AdmissionsAssistantMessage({
  message,
  onCitationClick,
  feedback,
  allowRetry = true,
  onRetry,
  onFeedback,
  onAction,
  activeRouteContext,
  onAssistantPolicyEvent,
  onOpenArtifactInGoogleSheets,
  onTaskProposalStart,
  onTaskProposalAskInChat,
  onTaskProposalDismiss,
  resolveDisclosureOpen,
  onDisclosureToggle,
  disclosureSessionId,
  showCopy = true,
  showFeedback = true,
  showRetry = true,
  resolveChartArtifactState,
  resolveVisualization3DArtifactState,
  googleWorkspaceBridgeStatus,
}: AdmissionsAssistantMessageProps & {
  onTaskProposalStart?: (proposalId: string) => void;
  onTaskProposalAskInChat?: (proposalId: string) => void;
  onTaskProposalDismiss?: (proposalId: string) => void;
  resolveDisclosureOpen?: (disclosureKey: string) => boolean;
  onDisclosureToggle?: (disclosureKey: string, open: boolean) => void;
  disclosureSessionId?: string | null;
  resolveChartArtifactState?: AssistantChartArtifactStateResolver;
  resolveVisualization3DArtifactState?: AssistantVisualization3DArtifactStateResolver;
  googleWorkspaceBridgeStatus?: AssistantGoogleWorkspaceBridgeStatus;
}) {
  const isUser = message.role === "user";
  const [copied, setCopied] = useState(false);
  const citations = message.citations ?? [];
  const visibleAssistantContent = useMemo(
    () =>
      isUser ? message.content : sanitizeVisibleAssistantText(message.content),
    [isUser, message.content],
  );
  const { sanitizedText } = useMemo(
    () =>
      isUser
        ? { sanitizedText: visibleAssistantContent, inlineLinks: [] }
        : stripInlineAssistantLinks(visibleAssistantContent),
    [isUser, visibleAssistantContent],
  );
  const responseSections = useMemo(
    () =>
      isUser
        ? { mainText: sanitizedText, explanationText: null }
        : splitAssistantResponseSections(sanitizedText),
    [isUser, sanitizedText],
  );
  const feedbackSubmission = useMemo(() => {
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    return (
      (metadata as AdmissionsAssistantMessageMetadata).feedbackSubmission ??
      null
    );
  }, [message.metadata]);
  const feedbackStatusCopy = useMemo(
    () =>
      feedbackSubmission ? resolveFeedbackStatusCopy(feedbackSubmission) : null,
    [feedbackSubmission],
  );
  const positiveFeedbackPopupTrigger = useMemo(() => {
    if (
      feedback !== "up" ||
      feedbackSubmission?.submissionStatus !== "accepted"
    ) {
      return null;
    }
    const feedbackId =
      typeof feedbackSubmission.feedbackId === "string"
        ? feedbackSubmission.feedbackId.trim()
        : "";
    const submittedAt =
      typeof feedbackSubmission.submittedAt === "string"
        ? feedbackSubmission.submittedAt.trim()
        : "";
    return (
      feedbackId ||
      submittedAt ||
      `accepted:${message.messageId ?? "assistant-feedback"}`
    );
  }, [
    feedback,
    feedbackSubmission?.feedbackId,
    feedbackSubmission?.submissionStatus,
    feedbackSubmission?.submittedAt,
    message.messageId,
  ]);
  const shouldShowRecentPositiveFeedbackPopup = useMemo(() => {
    if (!positiveFeedbackPopupTrigger) {
      return false;
    }
    const submittedAt =
      typeof feedbackSubmission?.submittedAt === "string"
        ? feedbackSubmission.submittedAt.trim()
        : "";
    if (!submittedAt) {
      return true;
    }
    const submittedAtMs = Date.parse(submittedAt);
    if (!Number.isFinite(submittedAtMs)) {
      return true;
    }
    return Date.now() - submittedAtMs <= POSITIVE_FEEDBACK_POPUP_RECENCY_MS;
  }, [feedbackSubmission?.submittedAt, positiveFeedbackPopupTrigger]);
  const [
    activePositiveFeedbackPopupTrigger,
    setActivePositiveFeedbackPopupTrigger,
  ] = useState<string | null>(null);
  const lastPositiveFeedbackPopupTriggerRef = useRef<string | null>(null);
  const showPositiveFeedbackPopup = Boolean(
    positiveFeedbackPopupTrigger &&
      activePositiveFeedbackPopupTrigger === positiveFeedbackPopupTrigger,
  );
  useEffect(() => {
    if (!positiveFeedbackPopupTrigger) {
      lastPositiveFeedbackPopupTriggerRef.current = null;
      setActivePositiveFeedbackPopupTrigger(null);
      return;
    }
    if (!shouldShowRecentPositiveFeedbackPopup) {
      lastPositiveFeedbackPopupTriggerRef.current =
        positiveFeedbackPopupTrigger;
      setActivePositiveFeedbackPopupTrigger(null);
      return;
    }
    if (
      lastPositiveFeedbackPopupTriggerRef.current ===
      positiveFeedbackPopupTrigger
    ) {
      return;
    }

    lastPositiveFeedbackPopupTriggerRef.current = positiveFeedbackPopupTrigger;
    setActivePositiveFeedbackPopupTrigger(positiveFeedbackPopupTrigger);
  }, [positiveFeedbackPopupTrigger, shouldShowRecentPositiveFeedbackPopup]);
  const renderHints = useMemo(() => {
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    const candidate = (metadata as AdmissionsAssistantMessageMetadata)
      .renderHints;
    return candidate && typeof candidate === "object" ? candidate : null;
  }, [message.metadata]);
  const artifactPreview = useMemo(() => {
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    const candidate = (metadata as AdmissionsAssistantMessageMetadata)
      .artifactPreview;
    return candidate && typeof candidate === "object" ? candidate : null;
  }, [message.metadata]);
  const jsxEmbed = useMemo<AdmissionsAssistantJsxEmbed | null>(() => {
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    const candidate = (metadata as AdmissionsAssistantMessageMetadata).jsxEmbed;
    return candidate && typeof candidate === "object" ? candidate : null;
  }, [message.metadata]);
  const taskProposal = useMemo(() => {
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    const candidate = (metadata as AdmissionsAssistantMessageMetadata)
      .taskProposal;
    return candidate && typeof candidate === "object" ? candidate : null;
  }, [message.metadata]);
  const feedbackContext = useMemo(() => {
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    const candidate = (metadata as AdmissionsAssistantMessageMetadata)
      .feedbackContext;
    return candidate && typeof candidate === "object" ? candidate : null;
  }, [message.metadata]);
  const presentationDiagnostics = useMemo(() => {
    if (
      feedbackContext &&
      typeof feedbackContext === "object" &&
      feedbackContext.persona === "parent"
    ) {
      return null;
    }
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    const candidate = (metadata as AdmissionsAssistantMessageMetadata)
      .presentationDiagnostics;
    return candidate && typeof candidate === "object" ? candidate : null;
  }, [feedbackContext, message.metadata]);
  const presentationRoute = useMemo(() => {
    if (feedbackContext?.route) {
      return feedbackContext.route;
    }
    for (const action of message.actions ?? []) {
      const targetRoute =
        typeof action.targetRoute === "string" ? action.targetRoute.trim() : "";
      if (targetRoute) {
        return targetRoute;
      }
    }
    return null;
  }, [feedbackContext?.route, message.actions]);
  const settingsIntent = useMemo(() => {
    const metadata = message.metadata;
    if (metadata && typeof metadata === "object" && !Array.isArray(metadata)) {
      if (
        (metadata as AdmissionsAssistantMessageMetadata).settingsIntent === true
      ) {
        return true;
      }
    }
    return resolveAssistantRouteFamily(presentationRoute).id === "settings";
  }, [message.metadata, presentationRoute]);
  const settingsSpecialistSnapshot = useMemo(
    () =>
      settingsIntent
        ? resolveAssistantSettingsSpecialistSnapshot(presentationRoute)
        : null,
    [presentationRoute, settingsIntent],
  );
  const taskThread = useMemo(() => {
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    const candidate = (metadata as AdmissionsAssistantMessageMetadata)
      .taskThread;
    return candidate && typeof candidate === "object" ? candidate : null;
  }, [message.metadata]);
  const taskThreadRollup = useMemo(() => {
    const metadata = message.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return null;
    }
    const candidate = (metadata as AdmissionsAssistantMessageMetadata)
      .taskThreadRollup;
    return candidate && typeof candidate === "object"
      ? (candidate as AdmissionsAssistantTaskThreadRollup)
      : null;
  }, [message.metadata]);
  const disclosureScope = useMemo(() => {
    const sessionScope = String(disclosureSessionId ?? "sessionless").trim();
    const taskThreadId =
      typeof taskThread?.threadId === "string"
        ? taskThread.threadId.trim()
        : "";
    const messageScope = String(
      message.messageId ?? `${message.role}-${message.content.slice(0, 48)}`,
    ).trim();
    return taskThreadId
      ? `${sessionScope}:thread:${taskThreadId}`
      : `${sessionScope}:message:${messageScope}`;
  }, [
    disclosureSessionId,
    message.content,
    message.messageId,
    message.role,
    taskThread?.threadId,
  ]);
  const buildDisclosureKey = useCallback(
    (sectionKey: string) => `${disclosureScope}:${sectionKey}`,
    [disclosureScope],
  );
  const resolveSectionOpen = useCallback(
    (sectionKey: string) =>
      resolveDisclosureOpen
        ? resolveDisclosureOpen(buildDisclosureKey(sectionKey))
        : undefined,
    [buildDisclosureKey, resolveDisclosureOpen],
  );
  const handleSectionToggle = useCallback(
    (sectionKey: string, open: boolean) => {
      onDisclosureToggle?.(buildDisclosureKey(sectionKey), open);
    },
    [buildDisclosureKey, onDisclosureToggle],
  );
  const readOnlyDegradedReason =
    taskThread?.tenantScopeStatus === "legacy_placeholder"
      ? (taskThread.degradedReason ??
        "Historical assistant record is read-only.")
      : null;
  const isChartTaskThread =
    !isUser && isChartArtifactWorkflowKey(taskThread?.workflowKey);
  const [hydratedChartPanels, setHydratedChartPanels] = useState<
    IntellioChartPanel[]
  >([]);
  const [chartHydrationState, setChartHydrationState] = useState<
    AiChartInlinePreviewState | "idle"
  >(() => {
    if ((artifactPreview?.chartPanels?.length ?? 0) > 0) {
      return "ready";
    }
    if (!artifactPreview?.chartArtifact) {
      return "idle";
    }
    return isChartTaskThread && taskThread?.state === "completed"
      ? "idle"
      : "loading";
  });
  const [resolvedChartArtifact, setResolvedChartArtifact] =
    useState<AdmissionsAssistantArtifactWorkspaceRef | null>(
      artifactPreview?.chartArtifact ?? null,
    );
  const [hydratedVisualization3DSpace, setHydratedVisualization3DSpace] =
    useState<IntellioVisualization3DSpaceModel | null>(
      artifactPreview?.visualization3DSpace ?? null,
    );
  const [hydratedVisualization3DPanels, setHydratedVisualization3DPanels] =
    useState<IntellioChartPanel[]>([]);
  const [resolvedVisualization3DArtifact, setResolvedVisualization3DArtifact] =
    useState<AdmissionsAssistantArtifactWorkspaceRef | null>(
      artifactPreview?.visualization3DArtifact ?? null,
    );
  const chartArtifact = artifactPreview?.chartArtifact ?? null;
  const chartArtifactRefreshKey =
    chartArtifact?.sourceRef ?? chartArtifact?.downloadUrl ?? null;
  const inlineChartPanels = artifactPreview?.chartPanels ?? [];
  const chartHydrationPolicy =
    chartArtifact && inlineChartPanels.length === 0
      ? (renderHints?.chartHydrationPolicy ?? "eager")
      : "eager";
  const resolveChartArtifactStateRef = useRef(resolveChartArtifactState);
  resolveChartArtifactStateRef.current = resolveChartArtifactState;
  const visualization3DArtifact =
    artifactPreview?.visualization3DArtifact ?? null;
  const visualization3DArtifactRefreshKey =
    visualization3DArtifact?.sourceRef ??
    visualization3DArtifact?.downloadUrl ??
    null;
  const inlineVisualization3DSpace =
    artifactPreview?.visualization3DSpace ?? null;
  const inlineVisualization3DPanels =
    artifactPreview?.visualization3DPanels ?? [];
  const visualization3DHydrationPolicy =
    visualization3DArtifact && !inlineVisualization3DSpace
      ? (renderHints as Record<string, unknown> | null)
          ?.visualization3DHydrationPolicy === "on_workspace_open"
        ? "on_workspace_open"
        : "eager"
      : "eager";
  const resolveVisualization3DArtifactStateRef = useRef(
    resolveVisualization3DArtifactState,
  );
  resolveVisualization3DArtifactStateRef.current =
    resolveVisualization3DArtifactState;

  useEffect(() => {
    setResolvedChartArtifact(chartArtifact);
    if (!chartArtifact?.downloadUrl) {
      setHydratedChartPanels([]);
      setChartHydrationState("idle");
      return;
    }
    if (inlineChartPanels.length > 0) {
      setHydratedChartPanels(inlineChartPanels);
      setChartHydrationState("ready");
      return;
    }
    if (chartHydrationPolicy === "on_workspace_open") {
      setHydratedChartPanels([]);
      setChartHydrationState("failed");
      return;
    }

    let cancelled = false;
    setChartHydrationState("loading");
    void (async () => {
      try {
        const resolvedState = await resolveAssistantChartArtifactState({
          chartArtifact,
          initialCharts: inlineChartPanels,
          refreshChartArtifactState: resolveChartArtifactStateRef.current,
          debugSource: "message-inline-hydration",
        });
        if (cancelled) {
          return;
        }
        setResolvedChartArtifact(resolvedState.chartArtifact);
        setHydratedChartPanels(resolvedState.chartPanels);
        setChartHydrationState(
          resolvedState.chartPanels.length > 0 ? "ready" : "failed",
        );
      } catch {
        if (!cancelled) {
          setHydratedChartPanels([]);
          setChartHydrationState("failed");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chartArtifactRefreshKey, chartHydrationPolicy, inlineChartPanels.length]);
  useEffect(() => {
    setResolvedVisualization3DArtifact(visualization3DArtifact);
    if (!visualization3DArtifact?.downloadUrl) {
      setHydratedVisualization3DSpace(inlineVisualization3DSpace);
      setHydratedVisualization3DPanels(inlineVisualization3DPanels);
      return;
    }
    if (inlineVisualization3DSpace) {
      setHydratedVisualization3DSpace(inlineVisualization3DSpace);
      setHydratedVisualization3DPanels(inlineVisualization3DPanels);
      return;
    }
    if (visualization3DHydrationPolicy === "on_workspace_open") {
      setHydratedVisualization3DSpace(null);
      setHydratedVisualization3DPanels([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const resolvedState =
          await resolveAssistantVisualization3DArtifactState({
            visualization3DArtifact,
            initialSpace: inlineVisualization3DSpace,
            initialPanels: inlineVisualization3DPanels,
            refreshVisualization3DArtifactState:
              resolveVisualization3DArtifactStateRef.current,
          });
        if (cancelled) {
          return;
        }
        setResolvedVisualization3DArtifact(
          resolvedState.visualization3DArtifact,
        );
        setHydratedVisualization3DSpace(resolvedState.visualization3DSpace);
        setHydratedVisualization3DPanels(resolvedState.visualization3DPanels);
      } catch {
        if (!cancelled) {
          setHydratedVisualization3DSpace(null);
          setHydratedVisualization3DPanels([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    visualization3DArtifactRefreshKey,
    visualization3DHydrationPolicy,
    inlineVisualization3DSpace,
    inlineVisualization3DPanels.length,
  ]);
  const chartPanels = artifactPreview?.chartPanels?.length
    ? artifactPreview.chartPanels
    : hydratedChartPanels;
  const visualization3DSpace =
    artifactPreview?.visualization3DSpace ?? hydratedVisualization3DSpace;
  const visualization3DPanels = artifactPreview?.visualization3DPanels?.length
    ? artifactPreview.visualization3DPanels
    : hydratedVisualization3DPanels;
  const resolvedArtifactPreview =
    useMemo<AdmissionsAssistantArtifactPreview | null>(() => {
      if (!artifactPreview) {
        return null;
      }
      return {
        ...artifactPreview,
        workbookPreview:
          coerceAssistantWorkbookInlinePreview(
            artifactPreview.workbookPreview,
          ) ?? null,
        chartArtifact:
          resolvedChartArtifact ?? artifactPreview.chartArtifact ?? null,
        chartPanels,
        visualization3DArtifact:
          resolvedVisualization3DArtifact ??
          artifactPreview.visualization3DArtifact ??
          null,
        visualization3DSpace,
        visualization3DPanels,
      };
    }, [
      artifactPreview,
      chartPanels,
      resolvedChartArtifact,
      resolvedVisualization3DArtifact,
      visualization3DSpace,
      visualization3DPanels,
    ]);

  const recommendedActions = useMemo(
    () => selectRecommendedAssistantActions(message.actions),
    [message.actions],
  );
  const explicitResultCards = useMemo(
    () =>
      renderHints?.resultCardMode === "exact_targets"
        ? buildAssistantResultCards({
            citations,
            actions: message.actions,
          })
        : [],
    [citations, message.actions, renderHints?.resultCardMode],
  );
  const responseSurfaceProjection = useMemo(
    () =>
      isUser
        ? {
            mainText: responseSections.mainText,
            projectedSections: [],
            suppressRelatedLinks: false,
            rawDataText: null,
          }
        : projectAdmissionsResponseSurface({
            mainText: responseSections.mainText,
            citations,
            actions: message.actions,
            artifactPreview: resolvedArtifactPreview,
          }),
    [
      citations,
      isUser,
      message.actions,
      resolvedArtifactPreview,
      responseSections.mainText,
    ],
  );
  const resultCards = explicitResultCards;
  const legacyExactTargetFacts = useMemo(
    () => buildLegacyExactTargetFacts(message.content, citations),
    [citations, message.content],
  );
  const composerAnswer =
    responseSurfaceProjection.projectedSections.length > 0
      ? responseSurfaceProjection.mainText
      : sanitizedText;
  const hasLocalArtifactPreview =
    !isUser &&
    Boolean(
      resolvedArtifactPreview?.workbookArtifact ||
        resolvedArtifactPreview?.spreadsheetDownloadArtifact ||
        resolvedArtifactPreview?.workbookPreview ||
        resolvedArtifactPreview?.chartArtifact ||
        (resolvedArtifactPreview?.chartPanels?.length ?? 0) > 0 ||
        resolvedArtifactPreview?.visualization3DArtifact ||
        resolvedArtifactPreview?.visualization3DSpace ||
        (resolvedArtifactPreview?.visualization3DPanels?.length ?? 0) > 0 ||
        resolvedArtifactPreview?.landingPageArtifact ||
        resolvedArtifactPreview?.landingPagePreview ||
        resolvedArtifactPreview?.diagramArtifact ||
        resolvedArtifactPreview?.diagramPreview ||
        (resolvedArtifactPreview?.artifactCards?.length ?? 0) > 0,
    );
  const hasTaskStructuredContent =
    !isUser &&
    (Boolean(taskThread) || Boolean(taskProposal) || hasLocalArtifactPreview);
  const hasChartPreviewPayload =
    !isUser &&
    Boolean(
      resolvedArtifactPreview?.chartArtifact ||
        (resolvedArtifactPreview?.chartPanels?.length ?? 0) > 0,
    );
  const chartInlinePreviewState: AiChartInlinePreviewState | null = !isUser
    ? chartPanels.length > 0
      ? "ready"
      : isChartTaskThread &&
          (taskThread?.state === "failed" || taskThread?.state === "cancelled")
        ? "failed"
        : isChartTaskThread && taskThread?.state === "completed"
          ? chartHydrationState === "loading"
            ? "loading"
            : "failed"
          : hasChartPreviewPayload
            ? chartHydrationState === "failed"
              ? "failed"
              : "loading"
            : isChartTaskThread
              ? "loading"
              : null
    : null;
  const suppressChartTaskThreadChrome = Boolean(
    chartInlinePreviewState && isChartTaskThread,
  );
  const chartOnlyAssistantDelivery = suppressChartTaskThreadChrome;
  const compactVisualization3DArtifactDelivery =
    !isUser &&
    Boolean(taskThread) &&
    taskThread?.state === "completed" &&
    Boolean(resolvedArtifactPreview?.visualization3DArtifact);
  const taskDetailsSummary =
    hasTaskStructuredContent &&
    !suppressChartTaskThreadChrome &&
    !compactVisualization3DArtifactDelivery
      ? responseSurfaceProjection.mainText
      : null;
  const resolvedRouteFamily = useMemo(
    () =>
      resolveAssistantRouteFamily(
        feedbackContext?.route ??
          (isRecord(activeRouteContext)
            ? String(activeRouteContext.pathname ?? "")
            : null),
      ),
    [activeRouteContext, feedbackContext?.route],
  );
  const explicitSharedPresentation = useMemo(
    () => {
      const normalized = normalizeAssistantPresentation(
        message.presentation
          ? {
              ...message.presentation,
              routeFamily:
                message.presentation.routeFamily ?? resolvedRouteFamily.id,
              module: message.presentation.module ?? resolvedRouteFamily.module,
            }
          : null,
      );
      return normalized
        ? repairAssistantPresentationForDisplay(normalized, message.content)
        : null;
    },
    [message.content, message.presentation, resolvedRouteFamily.id, resolvedRouteFamily.module],
  );
  const effectiveSharedPresentation = useMemo(() => {
    if (!explicitSharedPresentation) {
      return null;
    }
    const sectionTypes = new Set(
      explicitSharedPresentation.sections.map((section) => section.type),
    );
    const missingLocalStructuredSection =
      (hasLocalArtifactPreview && !sectionTypes.has("artifacts")) ||
      (Boolean(taskThread) && !sectionTypes.has("task_thread")) ||
      (Boolean(taskProposal) && !sectionTypes.has("task_proposal"));
    if (missingLocalStructuredSection) {
      return null;
    }
    return explicitSharedPresentation;
  }, [
    explicitSharedPresentation,
    hasLocalArtifactPreview,
    taskProposal,
    taskThread,
  ]);
  const presentationModel = useMemo(
    () =>
      isUser
        ? null
        : (effectiveSharedPresentation ??
          composeAssistantPresentation({
            answer: chartOnlyAssistantDelivery ? "" : composerAnswer,
            module: resolvedRouteFamily.module,
            routeFamilyId: resolvedRouteFamily.id,
            citations,
            actions: message.actions,
            artifactPreview: resolvedArtifactPreview,
            taskProposal,
            taskThread,
            taskThreadRollup,
            diagnostics: presentationDiagnostics,
            readOnlyDegradedReason,
            settingsSpecialistSnapshot,
            projectedSections: responseSurfaceProjection.projectedSections,
            suppressRelatedLinks:
              responseSurfaceProjection.suppressRelatedLinks ||
              Boolean(legacyExactTargetFacts),
            facts: legacyExactTargetFacts,
            debug: renderHints ? { renderHints } : null,
            clientProjectionUsed: true,
          }).presentation),
    [
      citations,
      effectiveSharedPresentation,
      isUser,
      message.actions,
      chartOnlyAssistantDelivery,
      presentationDiagnostics,
      readOnlyDegradedReason,
      resolvedRouteFamily.id,
      resolvedRouteFamily.module,
      resolvedArtifactPreview,
      renderHints,
      responseSurfaceProjection.projectedSections,
      responseSurfaceProjection.suppressRelatedLinks,
      composerAnswer,
      settingsSpecialistSnapshot,
      legacyExactTargetFacts,
      taskProposal,
      taskThread,
      taskThreadRollup,
    ],
  );
  const presentationModelForRendering = useMemo(() => {
    if (!presentationModel || !googleWorkspaceBridgeStatus) {
      return presentationModel;
    }
    let changed = false;
    const sections = presentationModel.sections.map((section) => {
      if (section.type !== "artifacts") {
        return section;
      }
      return {
        ...section,
        items: section.items.map((item) => {
          if (!isAssistantSpreadsheetArtifact(item)) {
            return item;
          }
          const googleWorkspace =
            buildAssistantGoogleWorkspaceAction(
              item,
              googleWorkspaceBridgeStatus,
            ) ??
            item.googleWorkspace ??
            null;
          if (googleWorkspace === item.googleWorkspace) {
            return item;
          }
          changed = true;
          return {
            ...item,
            googleWorkspace,
          };
        }),
      };
    });
    return changed ? { ...presentationModel, sections } : presentationModel;
  }, [googleWorkspaceBridgeStatus, presentationModel]);
  const clientProjectionUsed =
    !isUser && !effectiveSharedPresentation && Boolean(presentationModel);
  const hasStructuredContent =
    !isUser &&
    Boolean(
      presentationModel?.sections.some(
        (section) =>
          section.type !== "summary" && section.type !== "formatted_body",
      ),
    );
  const hasProjectedPrimarySurface =
    responseSurfaceProjection.projectedSections.some(
      (section) => section.type === "chart" || section.type === "table",
    );
  const rawProjectedDataText = !isUser
    ? responseSurfaceProjection.rawDataText
    : null;
  const showResponseActions =
    !taskProposal &&
    !hasTaskStructuredContent &&
    (showCopy || showFeedback || showRetry);
  const textRevealConfig: ResolvedAssistantTextRevealConfig | null =
    resolveAssistantTextRevealConfig({
      metadata: message.metadata,
      responseKind: presentationModel?.responseKind ?? null,
    });
  const presentationRevealText = collectAssistantPresentationRevealText(
    presentationModelForRendering,
  );
  const revealSourceText = isUser
    ? sanitizedText
    : presentationRevealText || responseSurfaceProjection.mainText;
  const revealTokens = useMemo(
    () => tokenizeRevealText(revealSourceText),
    [revealSourceText],
  );
  const revealMessageId = useMemo(
    () => resolveTextRevealMessageId(message.messageId),
    [message.messageId],
  );
  const revealTimerRef = useRef<number | null>(null);
  const revealStartedRef = useRef(false);
  const revealMessageIdRef = useRef<string | null>(revealMessageId);
  const shouldAnimateThisMountRef = useRef(
    revealMessageId ? !settledTextRevealMessageIds.has(revealMessageId) : true,
  );
  if (revealMessageIdRef.current !== revealMessageId) {
    revealMessageIdRef.current = revealMessageId;
    shouldAnimateThisMountRef.current = revealMessageId
      ? !settledTextRevealMessageIds.has(revealMessageId)
      : true;
    revealStartedRef.current = false;
  }
  const [revealPhase, setRevealPhase] = useState<
    "revealing" | "handoff" | "settled"
  >("settled");
  const [revealedTokenCount, setRevealedTokenCount] = useState(0);
  const revealStartDelayMs = textRevealConfig?.startDelayMs ?? 180;
  const revealWordDelayMs = textRevealConfig?.wordDelayMs ?? 34;
  const revealHandoffDelayMs =
    textRevealConfig?.structuredHandoffDelayMs ?? 120;
  const revealStructuredFadeMs = textRevealConfig?.structuredFadeMs ?? 160;
  const revealSourceContainsRawPayload =
    Boolean(rawProjectedDataText) &&
    revealSourceText.includes(String(rawProjectedDataText));
  const revealSourceLooksMachineFormatted =
    /\n\s*\|[^|\n]+\|[^|\n]*\n\s*\|[-:\s|]+\|/m.test(revealSourceText) ||
    /```/.test(revealSourceText) ||
    /\n\s*\|[^|\n]+\|[^|\n]*\n\s*\|[-:\s|]+\|/m.test(sanitizedText) ||
    /```/.test(sanitizedText);
  const revealSourceHasUnsafeRawProjection =
    !presentationRevealText &&
    (hasProjectedPrimarySurface ||
      revealSourceContainsRawPayload ||
      revealSourceLooksMachineFormatted);
  const shouldRevealText =
    !isUser &&
    shouldAnimateThisMountRef.current &&
    Boolean(textRevealConfig) &&
    !revealSourceHasUnsafeRawProjection &&
    revealTokens.length > 0;
  const revealedText = useMemo(
    () => revealTokens.slice(0, revealedTokenCount).join(""),
    [revealTokens, revealedTokenCount],
  );
  const revealedStreamingContent = useMemo(
    () =>
      formatMessage(
        revealedText,
        citations,
        onCitationClick,
        SCHOOL_SHELL_MESSAGE_FORMAT_STYLES,
      ),
    [citations, onCitationClick, revealedText],
  );
  const markRevealSettled = () => {
    if (revealMessageId) {
      settledTextRevealMessageIds.add(revealMessageId);
    }
  };

  useEffect(() => {
    if (revealTimerRef.current !== null) {
      window.clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }

    if (!shouldRevealText || !textRevealConfig) {
      setRevealPhase("settled");
      setRevealedTokenCount(revealTokens.length);
      return undefined;
    }

    let cancelled = false;
    let nextTokenIndex = Math.min(1, revealTokens.length);
    revealStartedRef.current = true;
    setRevealPhase("revealing");
    setRevealedTokenCount(nextTokenIndex);
    if (nextTokenIndex >= revealTokens.length) {
      markRevealSettled();
      if (revealHandoffDelayMs > 0) {
        setRevealPhase("handoff");
        revealTimerRef.current = window.setTimeout(() => {
          if (!cancelled) {
            setRevealPhase("settled");
          }
          revealTimerRef.current = null;
        }, revealHandoffDelayMs);
      } else {
        setRevealPhase("settled");
      }
      return undefined;
    }

    const tick = () => {
      if (cancelled) {
        return;
      }
      nextTokenIndex += 1;
      setRevealedTokenCount(nextTokenIndex);
      if (nextTokenIndex >= revealTokens.length) {
        markRevealSettled();
        if (revealHandoffDelayMs > 0) {
          setRevealPhase("handoff");
          revealTimerRef.current = window.setTimeout(() => {
            if (!cancelled) {
              setRevealPhase("settled");
            }
            revealTimerRef.current = null;
          }, revealHandoffDelayMs);
        } else {
          setRevealPhase("settled");
          revealTimerRef.current = null;
        }
        return;
      }
      revealTimerRef.current = window.setTimeout(tick, revealWordDelayMs);
    };

    revealTimerRef.current = window.setTimeout(tick, revealStartDelayMs);

    return () => {
      cancelled = true;
      revealStartedRef.current = false;
      if (revealTimerRef.current !== null) {
        window.clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, [
    revealHandoffDelayMs,
    revealMessageId,
    revealStartDelayMs,
    revealTokens,
    revealWordDelayMs,
    shouldRevealText,
  ]);

  const isRevealPendingText = shouldRevealText && !revealStartedRef.current;
  const isRevealingText =
    shouldRevealText &&
    (revealPhase === "revealing" ||
      revealPhase === "handoff" ||
      isRevealPendingText);
  const isStructuredHandoff = shouldRevealText && revealPhase === "handoff";
  const hasRevealedAnswerText = revealedText.trim().length > 0;
  const shouldShowContextActionRail =
    resultCards.length === 0 &&
    recommendedActions.length > 0 &&
    (!isRevealingText ||
      hasRevealedAnswerText ||
      Boolean(presentationModelForRendering));
  const shouldDelayStructuredSections = Boolean(
    textRevealConfig?.revealStructuredSections,
  );
  const useStableAssistantFrame =
    !isUser && (hasStructuredContent || isRevealingText || shouldRevealText);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(sanitizedText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.warn("[AdmissionsAI] Copy failed", error);
    }
  }, [sanitizedText]);

  const handleFeedback = useCallback(
    (value: "up" | "down") => {
      if (message.messageId) {
        onFeedback?.(message.messageId, value);
      }
    },
    [message.messageId, onFeedback],
  );

  const handleRetry = useCallback(() => {
    if (!message.messageId) {
      return;
    }
    onRetry?.(message.messageId);
  }, [message.messageId, onRetry]);

  return (
    <div
      className={
        isUser
          ? "flex flex-col items-end gap-2 animate-in fade-in duration-300"
          : "flex flex-col items-start gap-2 animate-in fade-in duration-300"
      }
      data-testid={isUser ? "chat-message-user" : "chat-message-assistant"}
      data-message-id={message.messageId ?? undefined}
      data-ai-message-variant="tailwind-school-shell"
      data-ai-school-shell-message={isUser ? "user" : "assistant"}
      data-ai-client-projection-used={clientProjectionUsed ? "true" : "false"}
    >
      <div
        className={[
          useStableAssistantFrame ? "w-full max-w-full" : "max-w-[88%]",
          "rounded-[22px] border px-4 py-3",
          isUser
            ? ["rounded-tr-md text-white", "chat-bubble-sent"].join(" ")
            : "rounded-tl-md border-gray-100 bg-white text-slate-900 shadow-sm",
        ].join(" ")}
        data-ai-structured-message={hasStructuredContent ? "true" : "false"}
      >
        {isUser ? (
          <p
            className={`${assistantStyles.chatBubbleParagraph} whitespace-pre-wrap text-[13px] text-white/95`}
          >
            {message.content}
          </p>
        ) : (
          <div className="space-y-3 break-words text-[13px] leading-[1.5]">
            {presentationModelForRendering ? (
              <div
                className={assistantStyles.messageStructuredContent}
                style={
                  {
                    "--ai-response-structured-fade-ms": `${revealStructuredFadeMs}ms`,
                  } as React.CSSProperties
                }
                data-testid="ai-message-structured-content"
              >
                <AssistantPresentationRenderer
                  presentation={presentationModelForRendering}
                  textRevealState={
                    isRevealingText
                      ? {
                          active: true,
                          content: revealedStreamingContent,
                          phase: isStructuredHandoff
                            ? "handoff"
                            : "revealing",
                          showCursor: Boolean(textRevealConfig?.cursor),
                        }
                      : null
                  }
                  formatTextContent={(text) =>
                    formatMessage(
                      repairAssistantAnswerForDisplayText(text),
                      citations,
                      onCitationClick,
                      SCHOOL_SHELL_MESSAGE_FORMAT_STYLES,
                    )
                  }
                  activeRouteContext={activeRouteContext}
                  onAssistantPolicyEvent={onAssistantPolicyEvent}
                  onOpenArtifactInGoogleSheets={onOpenArtifactInGoogleSheets}
                  sectionRenderers={{
                    task_proposal: () =>
                      taskProposal ? (
                        <TaskProposalSection
                          proposal={taskProposal}
                          onStart={onTaskProposalStart}
                          onAskInChat={onTaskProposalAskInChat}
                          onDismiss={onTaskProposalDismiss}
                        />
                      ) : null,
                    task_thread: () =>
                      taskThread && suppressChartTaskThreadChrome ? (
                        resolvedArtifactPreview?.chartArtifact ? null : (
                          <ChartPreviewSection
                            preview={resolvedArtifactPreview}
                            chartPanels={chartPanels}
                            previewState={chartInlinePreviewState ?? "loading"}
                            readOnlyDegradedReason={readOnlyDegradedReason}
                            suppressActions={suppressChartTaskThreadChrome}
                            onAction={onAction}
                          />
                        )
                      ) : taskThread &&
                        !compactVisualization3DArtifactDelivery ? (
                        <>
                          <TaskThreadSection
                            taskThread={taskThread}
                            detailDisclosureId={buildDisclosureKey(
                              "task-thread-live-status",
                            )}
                            detailsOpen={resolveSectionOpen(
                              "task-thread-live-status",
                            )}
                            onDetailsToggle={(open) =>
                              handleSectionToggle(
                                "task-thread-live-status",
                                open,
                              )
                            }
                          />
                          <TaskThreadDetailsSection
                            summary={taskDetailsSummary}
                            rollup={taskThreadRollup}
                            disclosureId={buildDisclosureKey(
                              "task-thread-rollup-details",
                            )}
                            open={resolveSectionOpen(
                              "task-thread-rollup-details",
                            )}
                            onToggle={(open) =>
                              handleSectionToggle(
                                "task-thread-rollup-details",
                                open,
                              )
                            }
                          />
                        </>
                      ) : null,
                    artifacts: () => (
                      <>
                        {resolvedArtifactPreview &&
                        (visualization3DSpace ||
                          resolvedArtifactPreview.visualization3DArtifact) ? (
                          <Visualization3DPreviewSection
                            preview={resolvedArtifactPreview}
                            visualization3DSpace={visualization3DSpace}
                            visualization3DPanels={visualization3DPanels}
                            deferredHydration={
                              visualization3DHydrationPolicy ===
                              "on_workspace_open"
                            }
                            compactArtifactDelivery={
                              compactVisualization3DArtifactDelivery
                            }
                            readOnlyDegradedReason={readOnlyDegradedReason}
                            onAction={onAction}
                          />
                        ) : null}
                        {chartInlinePreviewState &&
                        (resolvedArtifactPreview?.chartArtifact ||
                          chartPanels.length > 0) ? (
                          <ChartPreviewSection
                            preview={resolvedArtifactPreview}
                            chartPanels={chartPanels}
                            previewState={chartInlinePreviewState}
                            readOnlyDegradedReason={readOnlyDegradedReason}
                            suppressActions={suppressChartTaskThreadChrome}
                            onAction={onAction}
                          />
                        ) : null}
                        {resolvedArtifactPreview?.workbookArtifact ? (
                          <WorkbookPreviewSection
                            preview={resolvedArtifactPreview}
                            readOnlyDegradedReason={readOnlyDegradedReason}
                            onAction={onAction}
                          />
                        ) : null}
                        {resolvedArtifactPreview?.landingPageArtifact ? (
                          <LandingPagePreviewSection
                            preview={resolvedArtifactPreview}
                            readOnlyDegradedReason={readOnlyDegradedReason}
                          />
                        ) : null}
                        {resolvedArtifactPreview?.diagramArtifact ? (
                          <DiagramPreviewSection
                            preview={resolvedArtifactPreview}
                            readOnlyDegradedReason={readOnlyDegradedReason}
                          />
                        ) : null}
                        {(resolvedArtifactPreview?.artifactCards?.length ?? 0) >
                        0 ? (
                          <ArtifactCardListSection
                            artifacts={
                              resolvedArtifactPreview?.artifactCards ?? []
                            }
                            readOnlyDegradedReason={readOnlyDegradedReason}
                          />
                        ) : null}
                      </>
                    ),
                  }}
                />
              </div>
            ) : isRevealingText ? (
              <div
                className="min-h-[1.5em] text-[13px] leading-[1.5] text-slate-700"
                data-testid="ai-message-streaming-text"
                data-stream-state={
                  isStructuredHandoff ? "handoff" : "revealing"
                }
              >
                {revealedStreamingContent}
                {textRevealConfig?.cursor ? (
                  <span
                    className="ml-0.5 inline-block h-[1em] w-[2px] animate-pulse bg-primary align-[-0.12em]"
                    data-testid="ai-message-streaming-cursor"
                    aria-hidden="true"
                  />
                ) : null}
              </div>
            ) : revealPhase === "settled" && shouldRevealText ? (
              <div
                className="min-h-[1.5em] text-[13px] leading-[1.5] text-slate-700"
                data-testid="ai-message-content-assistant"
                data-stream-state="settled"
              >
                {formatMessage(
                  revealSourceText,
                  citations,
                  onCitationClick,
                  SCHOOL_SHELL_MESSAGE_FORMAT_STYLES,
                )}
              </div>
            ) : null}

            {!isRevealingText && rawProjectedDataText ? (
              <ResponseExplanationSection
                content={rawProjectedDataText}
                citations={citations}
                onCitationClick={onCitationClick}
                disclosureId={buildDisclosureKey("projected-raw-data")}
                label="See raw data (markdown)"
                contentMode="plain"
                open={resolveSectionOpen("projected-raw-data")}
                onToggle={(open) =>
                  handleSectionToggle("projected-raw-data", open)
                }
              />
            ) : null}

            {jsxEmbed ? (
              <section
                className="rounded-[18px] border border-emerald-100 bg-[linear-gradient(135deg,rgba(248,252,250,0.98),rgba(255,255,255,1))] p-4 shadow-[0_14px_32px_rgba(36,50,53,0.08)]"
                data-testid="ai-jsx-embed-preview"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary/70">
                      JSX Embed
                    </p>
                    <h4 className="mt-2 text-sm font-semibold text-slate-900">
                      {jsxEmbed.headline}
                    </h4>
                    <p className="mt-1 text-[12px] leading-5 text-irongrey/72">
                      {jsxEmbed.description}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full border border-primary/15 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary/70">
                    {jsxEmbed.componentName}
                  </span>
                </div>
                <div
                  className="mt-3 rounded-[18px] border border-slate-100 bg-white p-3 shadow-[0_12px_28px_rgba(36,50,53,0.06)]"
                  data-testid="ai-jsx-owned-preview-card"
                >
                  <p className="text-[12px] font-semibold text-slate-900">
                    {jsxEmbed.componentName}
                  </p>
                  <p className="mt-1 text-[12px] leading-5 text-slate-600">
                    Preview rendered through the owned assistant message
                    surface.
                  </p>
                </div>
              </section>
            ) : null}

            {shouldShowContextActionRail ? (
              <div
                className="border-t border-gray-100 pt-3"
                data-testid="ai-context-action-rail"
              >
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-irongrey/50">
                  Actions
                </p>
                <div className="flex flex-wrap gap-2">
                  {recommendedActions.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      className="chat-button-actions whitespace-nowrap rounded-full border bg-white px-3 py-1.5 text-[11px] font-semibold text-primary"
                      style={{ transition: AI_CONTEXT_ACTION_TRANSITION }}
                      data-testid="ai-context-action"
                      data-action-id={action.id}
                      onClick={() => onAction?.(action)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {showResponseActions &&
            (!isRevealingText || !shouldDelayStructuredSections) ? (
              <div
                className="flex items-center gap-2 border-t border-gray-100 pt-3"
                data-testid="ai-assistant-response-actions"
              >
                {showCopy ? (
                  <button
                    type="button"
                    className={responseActionButtonClass}
                    onClick={copyToClipboard}
                    data-testid="ai-action-copy"
                    aria-label="Copy"
                    title={copied ? "Copied" : "Copy"}
                  >
                    <Copy size={15} />
                  </button>
                ) : null}
                {showFeedback ? (
                  <div className="relative">
                    {showPositiveFeedbackPopup ? (
                      <>
                        <style>
                          {`
                            @keyframes ai-feedback-toast-enter {
                              from {
                                opacity: 0;
                                transform: translate(-50%, 4px);
                              }
                              to {
                                opacity: 1;
                                transform: translate(-50%, 0);
                              }
                            }
                            @keyframes ai-feedback-toast-exit {
                              from {
                                opacity: 1;
                                transform: translate(-50%, 0);
                              }
                              to {
                                opacity: 0;
                                transform: translate(-50%, 0);
                              }
                            }
                          `}
                        </style>
                        <div
                          className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 whitespace-nowrap rounded-lg bg-irongrey px-2.5 py-1 text-[10px] font-semibold text-white shadow-md"
                          style={{
                            opacity: 0,
                            transform: "translate(-50%, 4px)",
                            animation:
                              "ai-feedback-toast-enter 100ms ease-out forwards, ai-feedback-toast-exit 300ms ease-out 2100ms forwards",
                          }}
                          onAnimationEnd={(event) => {
                            if (
                              event.animationName !== "ai-feedback-toast-exit"
                            ) {
                              return;
                            }
                            setActivePositiveFeedbackPopupTrigger((current) =>
                              current === positiveFeedbackPopupTrigger
                                ? null
                                : current,
                            );
                          }}
                          data-testid="ai-feedback-toast"
                          role="status"
                        >
                          Thanks for your feedback!
                        </div>
                      </>
                    ) : null}
                    <button
                      type="button"
                      className={responseActionButtonClass}
                      onClick={() => handleFeedback("up")}
                      data-testid="ai-action-thumbsup"
                      aria-pressed={feedback === "up"}
                      aria-label="Thumbs up"
                      title="Thumbs up"
                      disabled={
                        feedbackSubmission?.submissionStatus === "submitting"
                      }
                    >
                      <ThumbsUp size={15} />
                    </button>
                  </div>
                ) : null}
                {showRetry ? (
                  <button
                    type="button"
                    className={`${responseActionButtonClass} ml-auto disabled:cursor-not-allowed disabled:opacity-40`}
                    onClick={handleRetry}
                    disabled={!allowRetry}
                    data-testid="ai-action-retry"
                    aria-label="Retry"
                    title="Retry"
                  >
                    <RefreshCw size={15} />
                  </button>
                ) : null}
              </div>
            ) : null}

            {!taskProposal && feedbackStatusCopy ? (
              <div
                className={`rounded-xl border px-3 py-2 text-[11px] leading-5 ${feedbackStatusCopy.toneClass} ${assistantStyles.nodisplay}`}
                hidden
                data-testid="ai-feedback-status"
                data-feedback-status={feedbackSubmission?.submissionStatus}
              >
                <p className="font-semibold">{feedbackStatusCopy.label}</p>
                {feedbackStatusCopy.detail ? (
                  <p className="mt-1 opacity-90">{feedbackStatusCopy.detail}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
